import { UserRole } from '@/hooks/useUserRole';

// Category group definitions
export interface CategoryGroup {
  id: string;
  name: string;
  description: string;
  icon: string;
  categories: string[];
  tier: 'core' | 'practitioner' | 'partner' | 'admin';
  gradient: string;
}

// Core categories available to all users (individual sessions only for regular users)
export const CORE_CATEGORIES = [
  'meditation',
  'anxiety',
  'sleep',
  'motivation',
  'memory',
  'massage', // body relaxation
  'be_still', // emotional reset
  'self_love',
  'fitness', // fitness & workout motivation
  'life_celebrations', // life celebrations
  'zen-af' // explicit meditation (18+)
] as const;

// Practitioner additional categories (requires practitioner+ role)
export const PRACTITIONER_ADDITIONAL_CATEGORIES = [
  'yoga-meditation'
] as const;

// Partner additional categories (requires partner+ role) 
export const PARTNER_ADDITIONAL_CATEGORIES = [
  'sexual-wellness-women',
  'couples-connection', 
  'relationship-decisions',
  'addiction-recovery-men'
] as const;

// Admin-only categories (requires admin role)
export const ADMIN_ONLY_CATEGORIES = [
  'news-meditation'
] as const;

// All categories (admin has access to everything)
export const ALL_CATEGORIES = [
  ...CORE_CATEGORIES, 
  ...PRACTITIONER_ADDITIONAL_CATEGORIES, 
  ...PARTNER_ADDITIONAL_CATEGORIES, 
  ...ADMIN_ONLY_CATEGORIES
] as const;

export type CoreCategory = typeof CORE_CATEGORIES[number];
export type PractitionerAdditionalCategory = typeof PRACTITIONER_ADDITIONAL_CATEGORIES[number];
export type PartnerAdditionalCategory = typeof PARTNER_ADDITIONAL_CATEGORIES[number];
export type AdminOnlyCategory = typeof ADMIN_ONLY_CATEGORIES[number];
export type AllCategory = typeof ALL_CATEGORIES[number];

// Category Groups Configuration
export const CATEGORY_GROUPS: CategoryGroup[] = [
  {
    id: 'mind-focus',
    name: 'Mind & Focus',
    description: 'Mental clarity, concentration, and mindfulness practices',
    icon: '🧠',
    categories: ['meditation', 'memory', 'be_still'],
    tier: 'core',
    gradient: 'from-emerald-500 to-teal-600'
  },
  {
    id: 'emotional-wellness',
    name: 'Emotional Wellness',
    description: 'Stress relief, anxiety support, and emotional balance',
    icon: '💙',
    categories: ['anxiety', 'motivation', 'self_love', 'life_celebrations', 'zen-af'],
    tier: 'core',
    gradient: 'from-blue-500 to-indigo-600'
  },
  {
    id: 'fitness-energy',
    name: 'Fitness & Energy',
    description: 'High-energy motivation and workout mental strength',
    icon: '💪',
    categories: ['fitness'],
    tier: 'core',
    gradient: 'from-orange-500 to-red-600'
  },
  {
    id: 'physical-wellness',
    name: 'Physical Wellness',
    description: 'Body relaxation, sleep support, and physical restoration',
    icon: '✨',
    categories: ['sleep', 'massage'],
    tier: 'core', 
    gradient: 'from-purple-500 to-pink-600'
  },
  {
    id: 'advanced-practice',
    name: 'Advanced Practice',
    description: 'Specialized meditation and yoga practices',
    icon: '🧘‍♀️',
    categories: ['yoga-meditation'],
    tier: 'practitioner',
    gradient: 'from-amber-500 to-orange-600'
  },
  {
    id: 'relationship-intimacy',
    name: 'Relationship & Intimacy',
    description: 'Connection, intimacy, and relationship support',
    icon: '💕',
    categories: ['sexual-wellness-women', 'couples-connection', 'relationship-decisions'],
    tier: 'partner',
    gradient: 'from-rose-500 to-pink-600'
  },
  {
    id: 'recovery-healing',
    name: 'Recovery & Healing',
    description: 'Specialized therapeutic support and healing practices',
    icon: '🌿',
    categories: ['addiction-recovery-men'],
    tier: 'partner',
    gradient: 'from-green-500 to-emerald-600'
  },
  {
    id: 'news-processing',
    name: 'News Processing',
    description: 'Advanced therapeutic processing of current events',
    icon: '📰',
    categories: ['news-meditation'],
    tier: 'admin',
    gradient: 'from-gray-500 to-slate-600'
  }
];

/**
 * Get accessible categories based on user role
 * Users: Individual access to 10 core categories only
 * Practitioners: Individual + Group access to core + zen + yoga
 * Partners: Individual + Group access to all categories EXCEPT news-meditation
 * Admin: Full access to everything
 */
export const getAccessibleCategories = (role: UserRole): string[] => {
  switch (role) {
    case 'admin':
      return [...ALL_CATEGORIES]; // Full access including news-meditation
    case 'partners':
      return [...CORE_CATEGORIES, ...PRACTITIONER_ADDITIONAL_CATEGORIES, ...PARTNER_ADDITIONAL_CATEGORIES]; // All except news-meditation
    case 'practitioner':
      return [...CORE_CATEGORIES, ...PRACTITIONER_ADDITIONAL_CATEGORIES]; // Core + zen + yoga
    case 'moderator':
      return [...CORE_CATEGORIES, ...PRACTITIONER_ADDITIONAL_CATEGORIES]; // Same as practitioner
    case 'user':
    case null:
      return [...CORE_CATEGORIES]; // Individual access to core categories only
    default:
      return [...CORE_CATEGORIES];
  }
};

/**
 * Check if user role has access to group sessions
 * Users: Individual sessions only
 * Practitioners, Partners, Admin: Both individual and group sessions
 */
export const hasGroupSessionAccess = (role: UserRole): boolean => {
  return ['admin', 'practitioner', 'partners'].includes(role || '');
};

/**
 * Check if user role has access to individual sessions for a category
 */
export const hasIndividualSessionAccess = (categoryId: string, role: UserRole): boolean => {
  const accessibleCategories = getAccessibleCategories(role);
  return accessibleCategories.includes(categoryId);
};

/**
 * Get accessible category groups based on user role
 */
export const getAccessibleGroups = (role: UserRole): CategoryGroup[] => {
  const accessibleCategoryIds = getAccessibleCategories(role);
  
  return CATEGORY_GROUPS.map(group => ({
    ...group,
    categories: group.categories.filter(categoryId => 
      accessibleCategoryIds.includes(categoryId)
    )
  })).filter(group => group.categories.length > 0);
};

/**
 * Get category group by category ID
 */
export const getCategoryGroup = (categoryId: string): CategoryGroup | null => {
  return CATEGORY_GROUPS.find(group => 
    group.categories.includes(categoryId)
  ) || null;
};

/**
 * Check if user has access to a specific category
 */
export const hasAccessToCategory = (categoryId: string, role: UserRole): boolean => {
  const accessibleCategories = getAccessibleCategories(role);
  return accessibleCategories.includes(categoryId);
};

/**
 * Get category tier name for display purposes
 */
export const getCategoryTier = (categoryId: string): 'core' | 'practitioner' | 'partner' | 'admin' => {
  if (CORE_CATEGORIES.includes(categoryId as CoreCategory)) {
    return 'core';
  }
  if (PRACTITIONER_ADDITIONAL_CATEGORIES.includes(categoryId as PractitionerAdditionalCategory)) {
    return 'practitioner';
  }
  if (PARTNER_ADDITIONAL_CATEGORIES.includes(categoryId as PartnerAdditionalCategory)) {
    return 'partner';
  }
  if (ADMIN_ONLY_CATEGORIES.includes(categoryId as AdminOnlyCategory)) {
    return 'admin';
  }
  return 'core'; // fallback
};